/**
 * Command Centre — Product Descriptions
 * ======================================
 *
 * Central knowledge-transfer copy for every tile on the Admin Analysis
 * home page. Rendered as hover tooltips by `InfoTooltipWrapper` so a
 * new admin (or a founder-successor) can hover any product and
 * understand what it does, who uses it, and why it matters. Feb 2026.
 *
 * Keep each description ≤ 3 short lines — the tooltip is 288px wide.
 */

export const CC_PRODUCT_DESCRIPTIONS = {
  'stripe-live-banner': {
    title: 'Stripe Live Payment Health',
    description:
      'Real-time status of Stripe payment ingestion. Green = payments flowing; red = webhook or key issue. Click through to Stripe dashboard on failure.',
  },
  'platform-health': {
    title: 'Platform Health',
    description:
      'One-glance uptime of the whole stack: Postmark deliverability, MongoDB latency, Claude API, Replicate video, NeMo Guardrails. Any red pill needs same-day attention.',
  },
  'ci-health-badge': {
    title: 'Test Suite Heartbeat',
    description:
      'Live green/red badge of the last `pytest tests/` run — pulls from the `test_health` Mongo collection updated by scripts/record_pytest_health.py. Green pulse = every regression test passed. Red = a failing suite; click into the CI log to triage. Auto-refreshes every 60 seconds.',
  },
  'stabilisation-log': {
    title: 'Stabilisation Log',
    description:
      'Central register of every production incident. Auto-populated by the alerting system + hand-edited by admins. Highest-priority tile — always shown first.',
  },
  'live-traction': {
    title: 'Live Traction (Investor-grade)',
    description:
      'Real-time trailing-30-day sign-ups, MRR, and retention that safely re-renders on any VC call. Frozen headline numbers used in the Board Deck below.',
  },
  'prospect-alerts': {
    title: 'Investor Gateway — Prospect Alerts',
    description:
      'Coral badge lights up when a VC / angel submits Express-Interest on the public /investors page. Contact them within the golden 24-hour window.',
  },
  'investor-heatmap': {
    title: 'Investor Submissions Heatmap',
    description:
      '28-day submissions sparkline + ticket-size clusters. Used to create warm FOMO on live VC calls ("we\'ve had 12 tickets over $100k this month").',
  },
  'investor-pulse-scheduler': {
    title: 'Investor Pulse Scheduler',
    description:
      'Manual "fire the weekly VC pulse now" button + ad-hoc scheduler. Skip waiting for the Monday 09:00 UTC cron — send a fresh pulse before a call.',
  },
  'newsletter-operations': {
    title: 'Public Newsletter — Ops Console',
    description:
      'TEST · Preview · Broadcast for the bi-weekly Resilience Newsletter. Tuesday 10:00 UTC auto-cron. Test to the founder first, then broadcast to the full list.',
  },
  'newsletter-queue-stack': {
    title: 'Newsletter Queue Stack',
    description:
      'Pre-load 4–8 newsletters (2 months / 1 quarter). Cadence keeps firing when the founder is on holiday. Click Load-into-editor to promote a draft.',
  },
  'newsletter-annual-calendar': {
    title: 'Newsletter Annual Calendar',
    description:
      '26 bi-weekly slots grouped by quarter. Click any tile to jump to the Queue Stack and start writing. Great for planning campaigns around launches.',
  },
  'postmark-suppressions': {
    title: 'Postmark Suppressions Watch',
    description:
      'Live view of hard-bounces, spam complaints, and unsubscribes from Postmark. One-click sync mirrors them into newsletter_subscribers so the next send skips them.',
  },
  'peer-helps': {
    title: 'Peer Helps — Last 24h',
    description:
      'Every Support Trophy minted in the last 24 hours (teen helps another teen). Sprint 207Q. Shows peer-to-peer culture is alive.',
  },
  'maya-manager': {
    title: 'Maya Manager (Practice Partner Control)',
    description:
      'Identity, scripted conversations, and mood dials for Maya — the anchor teen Practice Partner. Change her personality here; changes flow to every teen chat.',
  },
  'templates-fired': {
    title: 'Safety Templates Fired — Last 24h',
    description:
      'Engagement proof for the verbatim-template bypass rule. Shows which safety templates fired, on which prompts. Sprint 207W.',
  },
  'active-launch-funnel': {
    title: 'Active Launch Funnel',
    description:
      'End-to-end signup telemetry: landing → email → magic link → onboarding → first challenge. Spot drop-off points before the launch push.',
  },
  'privacy-shield-hits': {
    title: 'Privacy Shield Hits',
    description:
      'Sparkline of PII redactions triggered by the Privacy Shield (email/phone/address stripped from teen prompts before they hit any LLM). Sprint 208 Revised+.',
  },
  'triggered-declined': {
    title: 'Triggered-but-Declined Handovers',
    description:
      'Users who refused the empathic-handover consent prompt after a safety trigger. Surfaces gaps in our crisis follow-up coverage.',
  },
  'ava-behavior-studio': {
    title: 'AvA Behavior Studio',
    description:
      'Master-admin-only editor for the system prompts that shape EVERY Practice Partner response. Prompts are safety-critical — one wrong tweak affects thousands of teen chats.',
  },
  'rebecca-manager': {
    title: 'Rebecca Manager (Science Tutor Controller)',
    description:
      'Sister controller to Maya, for the Rebecca STEM tutor. Strict topic containment via NeMo Guardrails + a Python STEM allow-list. Feb 2026.',
  },
  'neo-manager': {
    title: 'Neo Manager (Challenges Companion Controller)',
    description:
      'Third fixed-anchor trainer alongside Maya + Rebecca. Neo walks teens through the 20-challenge social-fitness journey, celebrating Pivot Moments and unlocking Vault NFTs at Graduation. Edit persona, portrait, voice, and verbatim opener scripts. Feb 2026.',
  },
  'proactivity-leaderboard': {
    title: 'Proactive Champions Leaderboard',
    description:
      'Weekly / monthly top-10 teens across 5 signals (challenges, invites, helps, Rebecca chats, ideas). Review the list, then award 1st/2nd/3rd NFT bundles into the winners\' Vaults.',
  },
  'voice-of-user': {
    title: 'Voice of User — Ideas & Searches',
    description:
      'Ideas Inbox + Top Unmatched Searches. Free product-roadmap signal: what teens/adults want that we don\'t yet offer. Master admin only. Feb 2026.',
  },
  'founder-letter-editor': {
    title: 'Founder\'s Letter Editor',
    description:
      'Compose the monthly Founder\'s Letter that ships with the parent report and appears on /about. Master admin only.',
  },
  'board-deck': {
    title: 'Monthly Board Deck',
    description:
      'Auto-generated 12-slide board pack (traction, revenue, safety, roadmap) built from Live Traction numbers. Downloadable PDF. Master admin only.',
  },
  'security-status': {
    title: 'Security Status',
    description:
      'MFA coverage, failed-login rate, session fingerprints, admin API-key rotation age. Any amber pill needs weekly review.',
  },
  'recent-admin-activity': {
    title: 'Recent Admin Activity',
    description:
      'Immutable audit trail of every action taken by an admin in the last 7 days. Used for accountability + investigating anomalies.',
  },
  'persona-split': {
    title: 'Audience Split — Adult vs Teen',
    description:
      'Adult vs Teen active subscribers, kept separate so headline metrics never pool teens with adult professionals into one muddy "Total Active Users".',
  },
  'business-at-a-glance': {
    title: 'Business at a Glance',
    description:
      'Five headline metrics: total users, new sign-ups (7d), MRR, sessions (30d), and safety events (7d). Everything else on the page is a drill-down of these.',
  },
  'magic-link-health': {
    title: 'Magic-Link Health',
    description:
      'Passwordless-login funnel telemetry: email sent → click-through → session created. Any drop-off usually points to Postmark or DNS.',
  },
  'user-segments': {
    title: 'User Segments',
    description:
      'Breakdown by B2C Adults, B2C Teens, B2B Schools, B2B Business. Click any tile to filter the user browser to that segment.',
  },
  'at-risk-users': {
    title: 'At-Risk Users',
    description:
      'Users flagged as high or medium risk by the churn model (drop in session frequency, uncompleted challenges, negative sentiment). Worth a personal check-in.',
  },
  'business-health-score': {
    title: 'Business Health Score',
    description:
      'Composite 0–100 score from 4 pillars (Growth, Engagement, Retention, Revenue). Grade at a glance; expand each pillar to see the underlying metric.',
  },
  'retention-cohort-heatmap': {
    title: 'Retention Cohort Heatmap',
    description:
      'Week 0–7 retention for every weekly signup cohort. Green = healthy retention (>60%). Red rows point to onboarding regressions to investigate.',
  },
  'positive-churn': {
    title: 'Churn Watch',
    description:
      'HiBFF is a membership for life — churn is a warning signal, never a goal. This card separates goal-complete leavers from at-risk losses so we can reach out and win every member back.',
  },
  'lead-monitoring': {
    title: 'Lead Monitoring & Alerts',
    description:
      'Waitlist submissions, top search keywords, and Reddit lead-discovery alerts. Feeds the Reddit lead-discovery worker.',
  },
  'user-demographics': {
    title: 'User Demographics',
    description:
      'By region (email TLD), by role, by age band. Used to steer marketing spend and school-partnership outreach.',
  },
  'lead-generation': {
    title: 'Lead Generation Summary',
    description:
      'Weekly lead volume + qualified-lead conversion rate + Reddit API health. Click through for full Reddit + waitlist drill-down.',
  },
  'revenue-and-subscriptions': {
    title: 'Revenue & Subscriptions',
    description:
      'MRR / ARR trend chart + subscription tier pie (Free / Plus Monthly / Plus Annual) + hearts purchased. The chart clicks through to the full Revenue page.',
  },
  'social-growth-engagement': {
    title: 'Social Growth & Engagement',
    description:
      'Streak health, badge activity, and real-world "I Did This" challenge completions. Three of the strongest leading indicators for retention.',
  },
  'self-development-features': {
    title: 'Self-Development Features',
    description:
      'Adoption numbers for Dares, Journal, Role-Plays, Accountability Pods, Mentorships, and Real Meetups. Highlights which self-dev features are pulling their weight.',
  },
  'safety-system-health': {
    title: 'Safety & System Health',
    description:
      'Guardrail triggers (30d) + underlying system health: DB latency, Claude API status, error rate, payments backend. Any red = emergency page.',
  },
};

export default CC_PRODUCT_DESCRIPTIONS;
